import React, { PropsWithChildren, useRef } from "react";
import { GridApi, IServerSideGetRowsParams } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";

import { ButtonV2, useNotification } from "@fsg/gui-bits";
import { Status } from "@fsg/next-auth/types";

import { useRootModal } from "@app/components/RootModal/RootModal";
import { EntityApiQueryParams } from "@app/types";
import { buildQueryParams } from "@app/utils/getServerSideGridData";

import { useEngageServiceFactory } from "../useEngageServiceFactory";

type Props<ResponseType> = {
  transformParams?: (args: EntityApiQueryParams) => EntityApiQueryParams;
  service: ReturnType<typeof useEngageServiceFactory<any, ResponseType>>;
  resetGrid: (gridApi: GridApi<any>) => void;
};

export default function useExportAllData<ResponseType>(
  props: Props<ResponseType>
) {
  const { transformParams, service, resetGrid } = props;
  const [exportStatus, setExportStatus] = React.useState<Status>("idle");
  const notification = useNotification();

  const modal = useRootModal();

  const controllerRef = useRef<AbortController>();

  React.useEffect(() => {
    controllerRef.current = new AbortController();
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const handleCancel = React.useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      setExportStatus("idle");
      modal.api.setModalContent("Export cancelled");
      modal.api.close();
    }
  }, [modal.api]);

  return React.useCallback(
    async (ref: React.RefObject<AgGridReact>, fileName: string) => {
      const gridApi = ref.current?.api;
      if (exportStatus !== "idle" || !gridApi) return;

      modal.api.create({ onClose: handleCancel });
      modal.api.showModal();

      setExportStatus("loading");

      let currentDate = new Date().toJSON().slice(0, 10);
      const csvTitle = `Engage - ${currentDate}`;

      // Reset the AbortController for a new request
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      controllerRef.current = new AbortController();
      const signal = controllerRef.current.signal;

      try {
        await new Promise<void>((resolve, reject) => {
          // Since an SSRM grid is only loaded with a subset of the data as paginated by the request, to get and export all the data in the db, we can temporarily replace the grid's datasource option with a function that recursively gets all the data from the db.
          // Once all the data is available to the grid, the export method can be called. Finally the grid option is reset to the previous config.
          gridApi.setGridOption("serverSideDatasource", {
            getRows: async (params: IServerSideGetRowsParams) => {
              const queryParams = transformParams(buildQueryParams(params));
              try {
                modal.api.setModalContent(
                  <ModalContent>Preparing to export</ModalContent>
                );

                gridApi.showLoadingOverlay();

                // recursively get all data from db
                const response = await service.getAllData(
                  {
                    page: 1,
                    limit: 1000,
                    sort: queryParams.sort,
                    filter: queryParams.filter,
                  },
                  ({ fetched, total }) => {
                    // After each request completes, update the modal content to show progress
                    modal.api.setModalContent(
                      <ModalContent>
                        <div className="mb-[8px] text-lg">{`Retrieved ${fetched.length} / ${total} records`}</div>
                        <ButtonV2
                          size="md"
                          variant="secondary"
                          onClick={handleCancel}
                        >
                          Cancel
                        </ButtonV2>
                      </ModalContent>
                    );
                  },
                  [],
                  signal
                );
                // load all data into the table
                params.success({
                  rowData: response,
                  rowCount: response.length,
                });

                // Defer the export to allow AG Grid to process the data
                setTimeout(() => {
                  try {
                    // now that the grid has all the data, call grid export method
                    gridApi.exportDataAsCsv({
                      fileName: fileName ?? csvTitle,
                      allColumns: true,
                    });

                    notification.api.notify({
                      message: "Table exported successfully",
                      duration: 5000,
                      type: "success",
                    });
                    modal.api.close();
                    setExportStatus("idle");
                    resolve(); // Resolve the main promise indicating success
                  } catch (e) {
                    // Handle errors specifically from the export process
                    console.error("Error during exportDataAsCsv:", e);
                    notification.api.notify({
                      message: "Failed to generate CSV file.",
                      type: "warning",
                      duration: 5000,
                    });
                    setExportStatus("error");
                    gridApi.hideOverlay(); // If it was shown
                    // Reject the main promise; resetGrid will be called by the outer .catch()
                    reject(
                      e instanceof Error ? e : new Error("CSV export failed")
                    );
                  }
                }, 0); // A minimal delay to allow the event loop to process grid updates
              } catch (error) {
                params.fail();
                setExportStatus("error");
                gridApi.hideOverlay();
                reject(error);
                resetGrid(gridApi);
              }
            },
          });
        }).then(() => {
          resetGrid(gridApi);
        });
      } catch (error) {
        console.error(error);
        if ((error as Error).name === "AbortError") {
          notification.api.notify({
            message: "Export cancelled.",
            type: "warning",
            duration: 5000,
          });
        } else {
          notification.api.notify({
            message: "Error exporting data. Please try again.",
            type: "warning",
            duration: 5000,
          });
        }
        setExportStatus("idle");
        resetGrid(gridApi);
      }
    },
    [
      exportStatus,
      handleCancel,
      notification.api,
      resetGrid,
      service,
      transformParams,
      modal.api,
    ]
  );
}

export function ModalContent(props: PropsWithChildren) {
  return <div className="text-center pb-lg text-sm">{props.children}</div>;
}
